<template>
  <div :class="['si-home-page', { 'locale-ja': localeValue === 'ja' }]">
    <section class="hero">
      <div class="inner">
        <SIBreadcrumb
          :breadcrumb="pr.common.breadcrumb"
          :locale="localeValue"
          :show-products="false"
          :show-si="false"
          :current="pr.heroTitle"
        />
        <h1>{{ pr.heroTitle }}</h1>
        <p class="hero-subtitle">{{ pr.heroSubtitle }}</p>
        <p class="hero-desc">{{ pr.heroDesc }}</p>
      </div>
    </section>

    <section class="intro">
      <div class="inner narrow">
        <p
          v-viewportChanged="showView"
          class="hidden-element"
          v-for="(line, index) in pr.intro"
          :key="`intro-${index}`"
        >
          {{ line }}
        </p>
      </div>
    </section>

    <section class="solutions">
      <div class="inner">
        <SISectionTitle :title="pr.solutionsTitle" :subtitle="pr.solutionsSubtitle" />
        <div v-viewportChanged="showView" class="hidden-element solution-grid">
          <component
            :is="hasRoute(item, idx) ?'router-link' : 'div'"
            v-for="item in pr.solutions"
            :key="item.title"
            :to="toRoute(item)"
            :id="idx"
            class="solution-card"
          >
            <div class="icon-wrap">
              <img :src="getImageUrl(item.icon)" :alt="item.title" />
            </div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
            <p class="learn-more">
              <span>{{ pr.common.learnMore }}</span>
              <img :src="getImageUrl('si/si-icons/arrow-right.svg')" alt="" aria-hidden="true">
            </p>
          </component>
        </div>
      </div>
    </section>

    <section class="learn-more-fenrir inner">
      <SISectionTitle :title="pr.common.cta" />
      <BaseButton tag="router-link" variant="dark" size="hero" :to="contactLink">
        <span v-viewportChanged="showView" class="hidden-element">{{ pr.contact }}</span>
      </BaseButton>
    </section>
  </div>
</template>

<script>
import { computed, inject } from 'vue';
import BaseButton from '@/components/common/BaseButton.vue';
import SIBreadcrumb from '@/components/si/SIBreadcrumb.vue';
import SISectionTitle from '@/components/si/SISectionTitle.vue';
import useRenderData from '@/composables/useRenderData';

export default {
  name: 'si-page',
  components: {
    BaseButton,
    SIBreadcrumb,
    SISectionTitle,
  },
  setup() {
    const { pr, getImageUrl } = useRenderData('si');
    const locale = inject('locale');
    const localeValue = computed(() => locale?.value || '');
    const contactLink = computed(() => ({
      name: 'contact-us',
      params: {
        lang: localeValue.value || undefined,
      },
    }));

    const hasRoute = (si) => !!si.routeName;
    const toRoute = (si) => {
      if (!si.routeName) return undefined;
      return {
        name: si.routeName,
        params: {
          lang: localeValue.value || undefined,
        },
      };
    };

    return {
      pr,
      getImageUrl,
      localeValue,
      contactLink,
      hasRoute,
      toRoute,
    };
  },
};
</script>

<style lang="scss" scoped>
.si-home-page {
  .inner {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 24px;
    box-sizing: border-box;
  }

  .hero {
    background: #222222;
    padding: 130px 0 72px;

    h1 {
      margin: 28px 0 0;
      font-size: 32px;
      line-height: 44px;
      color: #ffffff;
      font-weight: 700;
    }

    .hero-subtitle {
      margin: 8px 0 0;
      color: #a7aeb1;
      font-size: 16px;
      line-height: 24px;
    }

    .hero-desc {
      margin: 18px 0 0;
      max-width: 760px;
      color: rgba(255, 255, 255, 0.88);
      font-size: 16px;
      line-height: 28px;
    }
  }

  .narrow {
    max-width: 800px;
  }

  .intro {
    padding: 84px 0 102px;

    p {
      margin: 0 0 16px;
      font-size: 16px;
      line-height: 30px;
      color: #212121;
      text-align: center;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .solutions {
    padding: 84px 0;
    background: #F7F9FA;

    .solution-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 24px;
    }

    .solution-card {
      border: 1px solid #e4e9eb;
      background: #ffffff;
      padding: 24px;
      display: flex;
      flex-direction: column;
      text-decoration: none;
      transition:
        transform 300ms cubic-bezier(0.165, 0.84, 0.44, 1),
        box-shadow 300ms cubic-bezier(0.165, 0.84, 0.44, 1);

      &:hover {
        opacity: 1;
        cursor: pointer;
        transform: translateY(-4px);
        box-shadow: 0 10px 20px 0 rgba(0, 0, 0, 0.1);

        .learn-more {
          color: #0f5ab6;
          opacity: 1;

          img {
            transform: translateX(4px);
          }
        }
      }

      .icon-wrap {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;

        img {
          width: 24px;
          height: 24px;
          object-fit: contain;
        }
      }

      h3 {
        margin: 16px 0 8px;
        color: #212121;
        font-size: 18px;
        line-height: 28px;
        font-weight: 700;
      }

      p {
        margin: 0;
        color: #666666;
        font-size: 14px;
        line-height: 24px;
        flex: 1;
      }

      .learn-more {
        margin-top: 18px;
        align-self: flex-end;
        min-height: 50px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        text-decoration: none;
        font-size: 12px;
        color: #1565c0;
        line-height: 18px;
        font-weight: 600;
        transition: background-color 200ms cubic-bezier(0.02, 0.01, 0.47, 1);

        img {
          width: 12px;
          height: 12px;
          filter: brightness(0);
          object-fit: contain;
          transition: transform 200ms cubic-bezier(0.02, 0.01, 0.47, 1);
        }
      }
    }
  }

  .learn-more-fenrir {
    margin-top: 102px;
    margin-bottom: 84px;
    text-align: center;
  }
}

.si-home-page.locale-ja {
  h1,
  h2,
  h3,
  p,
  a,
  span,
  strong {
    word-break: normal;
    overflow-wrap: break-word;
    line-break: strict;
  }
}

@media only screen and (max-width: 1024px) {
  .si-home-page {
    .solutions {
      .solution-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    }
  }
}

@media only screen and (max-width: 768px) {
  .si-home-page {
    .inner {
      padding: 0 12px;
    }

    .hero {
      padding: 108px 0 54px;

      h1 {
        margin-top: 18px;
        font-size: 28px;
        line-height: 38px;
      }

      .hero-desc {
        font-size: 14px;
        line-height: 24px;
      }
    }

    .solutions {
      padding: 60px 0;

      .solution-grid {
        grid-template-columns: 1fr;
        gap: 14px;
      }
    }
  }
}
</style>
